import type { DataBundle, HistoryEntry, Row, SectionDef } from '../types'
import { parseList } from './csv'
import { nextVersion } from './id'

export function nowIso(): string { return new Date().toISOString() }

export function prepareNewRow(section: SectionDef, initial: Row = {}): Row {
  const now = nowIso()
  const row: Row = {
    status: 'Borrador',
    version: 'V1',
    created_at: now,
    updated_at: now,
    created_by: 'Administradora',
    updated_by: 'Administradora',
    active: 'true',
    grade: '6° básico',
    vocabulary_level: 'Estándar',
    support_intensity: 'Sin apoyo adicional',
    difficulty: 'Intermedia',
    image_status: 'Pendiente de subir a GitHub',
    ...initial,
  }
  section.fields.forEach((field) => {
    if (row[field.key] === undefined) row[field.key] = field.type === 'boolean' ? 'false' : ''
  })
  return row
}

export function prepareDuplicate(section: SectionDef, source: Row): Row {
  const row = prepareNewRow(section, {
    ...source,
    [section.keyField]: '',
    derived_from: source[section.keyField] || '',
    version: 'V1',
    status: 'Borrador',
  })
  return row
}

export function prepareVersion(source: Row): Row {
  return {
    ...source,
    derived_from: source.derived_from || source.id || '',
    version: nextVersion(source),
    status: 'Borrador',
    updated_at: nowIso(),
    updated_by: 'Administradora',
  }
}

export function changedFields(before: Row | null, after: Row | null): string[] {
  const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])
  return [...keys].filter((key) => String(before?.[key] ?? '') !== String(after?.[key] ?? ''))
}

export function createHistory(
  section: SectionDef,
  action: HistoryEntry['action'],
  before: Row | null,
  after: Row | null,
): HistoryEntry {
  const entityId = String(after?.[section.keyField] || before?.[section.keyField] || '')
  return {
    id: crypto.randomUUID(),
    entityType: section.id,
    entityId,
    version: String(after?.version || before?.version || 'V1'),
    action,
    changedFields: changedFields(before, after),
    before,
    after,
    createdAt: nowIso(),
    createdBy: 'Administradora',
  }
}

export function entityDependencies(section: SectionDef, row: Row, data: DataBundle): string[] {
  const id = row[section.keyField] || ''
  const dependencies: string[] = []
  if (section.id === 'sources') {
    Object.entries(data.files).forEach(([fileName, rows]) => {
      const count = rows.filter((item) => item.source_id === id).length
      if (count) dependencies.push(`${count} pregunta(s) o material(es) en ${fileName}`)
    })
  }
  if (section.id === 'materials') {
    const count = data.sessions.filter((session) => session.support_material_id === id).length
    if (count) dependencies.push(`${count} sesión(es)`)
  }
  if (section.id === 'sessions') {
    const students = data.students.filter((student) => student.active_session_id === id || parseList(student.pending_session_ids || '').includes(id))
    if (students.length) dependencies.push(`${students.length} estudiante(s)`)
  }
  if (section.id === 'topics') {
    const topic = row.topic || ''
    const count = Object.values(data.files).flat().filter((item) => item.topic === topic && item !== row).length
    if (count) dependencies.push(`${count} registro(s) usan este tema`)
  }
  if (section.id.includes('question') || ['multiple-choice','true-false','ordering','matching','sources-questions','development'].includes(section.id)) {
    const sessions = data.sessions.filter((session) => parseList(session.simulated_question_ids || '').includes(id))
    if (sessions.length) dependencies.push(`${sessions.length} sesión(es) simuladas`)
  }
  return dependencies
}

export function isPublished(row: Row): boolean {
  return ['Activo', 'Listo para usar'].includes(row.status || '')
}
